import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n/I18nContext';
import { useProMessages } from '../i18n/pro';
import { createWithdrawRequest } from '../firebase/firestore';
import { ledgerApi } from '../api/ledger';
import './CreateWithdraw.css';

interface BalanceView {
  total: number;
  availableStars: number;
  pendingStars: number;
  waitingStars: number;
}

const CreateWithdraw: React.FC = () => {
  const navigate = useNavigate();
  const { user, firebaseUser } = useAuth();
  const { t } = useI18n();
  const proLabels = useProMessages().app;
  const [amount, setAmount] = useState<string>('');
  const [tonAddress, setTonAddress] = useState<string>('');
  const [useRegisteredAddress, setUseRegisteredAddress] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [balance, setBalance] = useState<BalanceView | null>(null);

  useEffect(() => {
    if (firebaseUser) {
      loadBalance();
    }
    if (user?.agent?.tonAddress) {
      setTonAddress(user.agent.tonAddress);
    }
  }, [firebaseUser, user]);

  const loadBalance = async () => {
    try {
      const data = await ledgerApi.getBalance();
      setBalance({
        total: data.total || 0,
        availableStars: data.availableStars || 0,
        pendingStars: data.pendingStars || 0,
        waitingStars: data.waitingStars || 0,
      });
    } catch (err) {
      console.error('Failed to load balance', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firebaseUser) {
      setError(t.app.common.mustBeLoggedIn);
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const numAmount = parseInt(amount, 10);
      if (isNaN(numAmount) || numAmount <= 0 || !Number.isInteger(numAmount)) {
        throw new Error(t.app.createWithdraw.amountMustBeInteger);
      }

      const finalAddress = useRegisteredAddress ? undefined : tonAddress;

      await createWithdrawRequest({
        amountStars: numAmount,
        tonAddress: finalAddress,
      });

      navigate('/withdraws');
    } catch (err: any) {
      setError(err.message || t.app.createWithdraw.createError);
    } finally {
      setLoading(false);
    }
  };

  const minWithdraw = 100;
  const availableStars = balance?.availableStars || 0;
  const waitingStars = balance?.waitingStars || 0;
  const pendingStars = balance?.pendingStars || 0;
  const withdrawAmount = parseInt(amount, 10) || 0;
  // Formule Pro active : aucune commission sur les retraits.
  const isPro = !!user?.proUntil && user.proUntil.getTime() > Date.now();
  const platformFeeBps = isPro ? 0 : 300;
  const estimatedFee = withdrawAmount > 0 ? Math.floor((withdrawAmount * platformFeeBps) / 10000) : 0;
  const estimatedNet = withdrawAmount > 0 ? withdrawAmount - estimatedFee : 0;

  return (
    <div className="create-withdraw">
      <h1>{t.app.createWithdraw.title}</h1>

      <div className="balance-info">
        <h3>{t.app.createWithdraw.balanceSource}</h3>
        <p className="balance-amount">{t.app.createWithdraw.available.replace('{amount}', String(availableStars))}</p>
        <p className="balance-note">{t.app.createWithdraw.pending.replace('{amount}', String(pendingStars))}</p>
        <p className="balance-note">{t.app.createWithdraw.waiting.replace('{amount}', String(waitingStars))}</p>
        <p className="balance-note">
          {t.app.createWithdraw.minAndFee.replace('{min}', String(minWithdraw))}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="withdraw-form">
        <div className="form-group">
          <label>{t.app.createWithdraw.amountLabel}</label>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            min={minWithdraw}
            step="1"
            required
            className="form-control"
            placeholder={t.app.createWithdraw.minPlaceholder.replace('{min}', String(minWithdraw))}
          />
          {amount && withdrawAmount > 0 && (
            <span className="form-hint">
              {(isPro ? proLabels.withdrawFeeHint : t.app.createWithdraw.feeHint)
                .replace('{gross}', String(withdrawAmount))
                .replace('{fee}', String(estimatedFee))
                .replace('{net}', String(estimatedNet))}
            </span>
          )}
        </div>

        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={useRegisteredAddress}
              onChange={(e) => setUseRegisteredAddress(e.target.checked)}
              className="checkbox"
            />
            {t.app.createWithdraw.useRegistered}
          </label>
          {user?.agent?.tonAddress && useRegisteredAddress && (
            <div className="registered-address">
              <strong>{t.app.createWithdraw.registeredAddress}</strong>
              <code>{user.agent.tonAddress}</code>
            </div>
          )}
        </div>

        {!useRegisteredAddress && (
          <div className="form-group">
            <label>{t.app.createWithdraw.tonDestination}</label>
            <input
              type="text"
              value={tonAddress}
              onChange={(e) => setTonAddress(e.target.value)}
              required={!useRegisteredAddress}
              className="form-control"
              placeholder={t.app.createWithdraw.tonPlaceholder}
            />
            <span className="form-hint">
              {t.app.createWithdraw.tonFormatHint}
            </span>
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        <div className="form-actions">
          <button
            type="submit"
            disabled={loading || availableStars < minWithdraw || !firebaseUser}
            className="submit-btn"
          >
            {loading ? t.app.createWithdraw.creating : t.app.createWithdraw.createRequest}
          </button>
          <button
            type="button"
            onClick={() => navigate('/withdraws')}
            className="cancel-btn"
          >
            {t.app.createWithdraw.cancel}
          </button>
        </div>

        {availableStars < minWithdraw && (
          <div className="warning-message">
            {t.app.createWithdraw.insufficientBalance.replace('{min}', String(minWithdraw))}
          </div>
        )}
      </form>
    </div>
  );
};

export default CreateWithdraw;
